/**
 * What the client reads about their US tax form (S-31): the status of each state, the plain words for
 * why a form stopped being valid, the lines of the form in the order the IRS prints them, and when the
 * signing screen lets them sign. Pure functions, so the rules are tested without a screen.
 *
 * Client vocabulary (S-22): never "fund", "portfolio" or "advisor"; "series", "holdings",
 * "Account Executive". Never a status code; the sealed words of the declaration are not here, they
 * come from the server and are shown as they are.
 */
import axios from 'axios';
import type {
  PrintedLines,
  TaxFormItem,
  TaxFormKind,
  TaxFormPreview,
  TaxFormState,
} from '../services/taxForms.service';
import { calendarDate } from '../components/client/enteringFund.helpers';

/** Where the client reviews and signs; the product (when known) tells the server which series. */
export function taxFormPath(productId: string | null = null): string {
  const query = productId ? `?product=${productId}` : '';
  return `/profile${query}#us-documents`;
}

/** True when a request was refused because new business needs a current tax form. */
export function isTaxFormRequired(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const detail: unknown = err.response?.data?.detail;
  return (
    err.response?.status === 409
    && typeof detail === 'object'
    && detail !== null
    && (detail as { code?: unknown }).code === 'tax_form_required'
  );
}

const YOUR_CITIZENSHIP = 'Your citizenship';
const SIGN_AGAIN = 'Review and sign again';

const REASONS: Record<string, string> = {
  name_changed: 'Your name',
  citizenship_changed: YOUR_CITIZENSHIP,
  residence_changed: 'Your country of residence',
  address_changed: 'Your address',
  birth_date_changed: 'Your date of birth',
  foreign_tin_changed: 'Your tax number',
  us_tin_changed: 'Your U.S. tax number',
  form_type_changed: 'The form you owe',
  us_phone_only: 'Your contact details',
  us_birth: 'Your place of birth',
  us_citizenship: YOUR_CITIZENSHIP,
};

/** What on the profile changed, in plain words, each once. Each is shown as a line of its own, so
 *  the Spanish screen translates it whole instead of splicing English into a sentence. */
export function changedThings(reasons: readonly string[]): string[] {
  return [...new Set(reasons.map((r) => REASONS[r]).filter((r): r is string => !!r))];
}

export interface StatusLine {
  /** The one-line state. */
  label: string;
  /** What to do or what it means. */
  detail: string;
  /** The label of the button, when there is one. */
  action: string | null;
}

const STATES: Record<TaxFormState, (item: TaxFormItem) => StatusLine> = {
  not_required: () => ({
    label: 'Not needed', detail: 'You do not owe a tax form right now.', action: null,
  }),
  not_signed: () => ({
    label: 'Not signed yet',
    detail: 'Sign it before you start a contribution to a U.S. series. We fill it in from your profile.',
    action: 'Review and sign',
  }),
  valid: (item) => ({
    label: item.valid_through ? `Valid through ${calendarDate(item.valid_through)}` : 'Signed',
    detail: item.signed_on ? `You signed it on ${calendarDate(item.signed_on)}.` : '',
    action: null,
  }),
  renewal_due: (item) => ({
    label: item.valid_through ? `Renewal due — valid through ${calendarDate(item.valid_through)}` : 'Renewal due',
    detail:
      'Review it and sign again. If nothing on your profile changed, it is already filled in and takes a minute.',
    action: SIGN_AGAIN,
  }),
  expired: () => ({
    label: 'Expired',
    detail: 'Review it and sign again before you start a new contribution to a U.S. series.',
    action: SIGN_AGAIN,
  }),
  invalidated: () => ({
    label: 'Needs to be signed again',
    detail:
      'Information on your profile changed, so the form you signed no longer matches it. '
      + 'Sign it again before you start a new contribution to a U.S. series.',
    action: SIGN_AGAIN,
  }),
};

export function taxFormStatusLine(item: TaxFormItem): StatusLine {
  return STATES[item.state](item);
}

export interface BannerCopy {
  headline: string;
  detail: string;
  /** The same sentence with the date the form is valid through, when the server sent one. */
  detailWithDeadline?: (date: string) => string;
}

const RENEWAL_DETAIL =
  'Please review it and sign again. If nothing on your profile changed, it is already filled in and takes a minute.';

/** The banner of a tax form (S-31): present from six months before it expires, or the day it stops
 *  being valid, until the client signs again. Null for any other reason. */
export function taxFormBannerCopy(reason: string): BannerCopy | null {
  if (reason === 'tax_form_renewal') {
    return {
      headline: 'Your U.S. tax form is due for renewal',
      detail: RENEWAL_DETAIL,
      detailWithDeadline: (date) =>
        `Please review it and sign again. If nothing on your profile changed, it is already filled in and takes a minute. It is valid through ${date}.`,
    };
  }
  if (reason === 'tax_form_invalid') {
    return {
      headline: 'Your U.S. tax form needs to be signed again',
      detail:
        'Please review it and sign again before you start a new contribution to a U.S. series. '
        + 'Your existing holdings, withdrawals and balances are not affected.',
    };
  }
  return null;
}

/** The title of the screen where the client reviews and signs, per form. */
export const SIGN_TITLE: Record<TaxFormKind, string> = {
  w8ben: 'Form W-8BEN — review and sign',
  w9: 'Form W-9 — review and sign',
};

/** The title of the row in "U.S. documents", per form. */
export const ROW_TITLE: Record<TaxFormKind, string> = {
  w8ben: 'U.S. tax form — Form W-8BEN',
  w9: 'U.S. tax form — Form W-9',
};

const MISSING: Record<string, string> = {
  name: 'Your legal name',
  residence: 'Your country of residence',
  address: 'Your address',
  citizenship: YOUR_CITIZENSHIP,
  us_tin: 'Your U.S. tax number (SSN or ITIN)',
};

/** What the profile lacks, one line each (shown as a list, translated whole). */
export function missingThings(missing: readonly string[]): string[] {
  return missing.map((m) => MISSING[m] ?? m);
}

export interface LineRow {
  /** The number the IRS prints. */
  line: string;
  label: string;
  value: string;
}

function text(lines: PrintedLines, key: string): string {
  const value = lines[key];
  return typeof value === 'string' ? value : '';
}

const W8BEN_ROWS: ReadonlyArray<[string, string, string]> = [
  ['1', 'Name of individual who is the beneficial owner', 'line1_name'],
  ['2', 'Country of citizenship', 'line2_citizenship'],
  ['3', 'Permanent residence address', 'line3_street'],
  ['', 'City or town, state or province, postal code', 'line3_city'],
  ['', 'Country', 'line3_country'],
  ['5', 'U.S. taxpayer identification number (SSN or ITIN), if required', 'line5_us_tin'],
  ['6a', 'Foreign tax identifying number', 'line6a_ftin'],
  ['8', 'Date of birth (MM-DD-YYYY)', 'line8_birth_date'],
  ['9', 'Country of residence for the income tax treaty with the United States', 'line9_treaty_country'],
];

const W9_ROWS: ReadonlyArray<[string, string, string]> = [
  ['1', 'Name', 'line1_name'],
  ['3a', 'Federal tax classification', 'line3a_classification'],
  ['5', 'Address (number, street, and apt. or suite no.)', 'line5_address'],
  ['6', 'City, state, and ZIP code', 'line6_city_state_zip'],
  ['', 'Social security number or ITIN', 'part1_tin'],
];

const CLASSIFICATION: Record<string, string> = { individual: 'Individual / sole proprietor' };

/** The lines of the form, in the order the IRS prints them, read-only. A line the file does not hold
 *  is shown empty (line 4 and line 10 stay empty on purpose). */
export function lineRows(form: TaxFormKind, lines: PrintedLines): LineRow[] {
  const rows = form === 'w8ben' ? W8BEN_ROWS : W9_ROWS;
  return rows.map(([line, label, key]) => {
    const raw = text(lines, key);
    return { line, label, value: CLASSIFICATION[raw] ?? raw };
  });
}

/** Where the client corrects what the form shows: a request to correct their data (the onboarding
 *  page of an approved client edits nothing), never the form. */
export const CORRECTION_PATH = '/client/data-requests';

export interface SigningInput {
  signerName: string;
  confirmed: boolean;
  usTin: string;
  mfaCode: string;
  mailingOpen: boolean;
  mailingStreet: string;
  mailingCountry: string;
}

/** Whether the signing screen lets the client press Sign. The server decides all of it again. */
export function canSign(preview: TaxFormPreview, input: SigningInput): boolean {
  if (!preview.applies || !preview.certification) return false;
  if ((preview.missing ?? []).length > 0 || (preview.contradicts ?? []).length > 0) return false;
  if (!input.confirmed || input.signerName.trim().length < 2) return false;
  if (preview.step_up_required && !/^\d{6}$/.test(input.mfaCode)) return false;
  const typed = input.usTin.replace(/\D/g, '');
  if (preview.needs_us_tin && preview.us_tin_required && typed.length !== 9) return false;
  if (typed.length > 0 && typed.length !== 9) return false;
  if (input.mailingOpen && (!input.mailingStreet.trim() || input.mailingCountry.length !== 2)) {
    return false;
  }
  return true;
}
